import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? "bg-background-50/98 backdrop-blur-lg shadow-sm"
            : "bg-background-50/92 backdrop-blur-sm"
        }`}
        style={{ height: "60px" }}
      >
        <div className="h-full flex items-center justify-between px-4 md:px-12">
          {/* Left: Brand + Nav links (desktop) */}
          <div className="flex items-center gap-6 md:gap-10">
            <Link
              to="/"
              className="text-xs font-bold tracking-[0.3em] text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              HIPOBUY
            </Link>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap font-sans"
              >
                <i className="ri-home-line mr-1.5"></i>
                Accueil
              </Link>
              <Link
                to="/article/guide-shopping"
                className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap font-sans"
              >
                <i className="ri-shopping-bag-line mr-1.5"></i>
                Guide Shopping
              </Link>
            </div>
          </div>

          {/* Right: Category dropdown (desktop) + Hamburger */}
          <div className="flex items-center gap-3">
            {/* Desktop dropdown */}
            <div className="relative hidden md:block">
              <select
                onChange={(e) => {
                  const url = e.target.value;
                  if (url) {
                    window.open(url, "_blank", "noopener,noreferrer");
                    e.target.value = "";
                  }
                }}
                defaultValue=""
                className="appearance-none bg-background-100 border border-background-300/70 text-foreground-700 text-sm rounded-full px-5 py-2.5 pr-10 cursor-pointer hover:border-foreground-300 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300/50 font-sans"
              >
                <option value="" disabled>
                  Choisir une catégorie
                </option>
                {categories.map((cat) => (
                  <option key={cat.name} value={cat.url}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-down-s-line text-foreground-500 text-sm"></i>
              </div>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors"
              aria-label="Menu"
            >
              {mobileMenuOpen ? (
                <i className="ri-close-line text-foreground-800 text-xl"></i>
              ) : (
                <i className="ri-menu-line text-foreground-800 text-xl"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-foreground-950/30"
            onClick={() => setMobileMenuOpen(false)}
          ></div>

          {/* Menu panel */}
          <div className="absolute top-[60px] left-0 right-0 bg-background-50 shadow-lg max-h-[calc(100vh-60px)] overflow-y-auto rounded-b-3xl">
            <div className="p-5 space-y-5">
              {/* Quick nav links */}
              <div className="space-y-1">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors text-foreground-800 font-medium font-sans"
                >
                  <i className="ri-home-line text-lg"></i>
                  Accueil
                </Link>
                <Link
                  to="/article/guide-shopping"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors text-foreground-800 font-medium font-sans"
                >
                  <i className="ri-shopping-bag-line text-lg"></i>
                  Guide Shopping
                </Link>
              </div>

              <div className="border-t border-background-200/60 pt-4">
                <p className="px-4 text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 font-sans">
                  Catégories
                </p>
                <div className="space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-background-100 transition-colors text-foreground-700 text-sm font-sans"
                    >
                      <i className="ri-arrow-right-up-line text-foreground-400"></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>

              {/* SEO Articles mobile */}
              <div className="border-t border-background-200/60 pt-4">
                <p className="px-4 text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 font-sans">
                  Articles SEO
                </p>
                <div className="space-y-0.5">
                  {[
                    { slug: "guide-complet", label: "Guide complet 2026" },
                    { slug: "vs-excel", label: "Vs Excel" },
                    { slug: "economiser", label: "Comment économiser" },
                    { slug: "debutants", label: "Pour débutants" },
                    { slug: "astuces", label: "Astuces avancées" },
                    { slug: "famille", label: "Pour les familles" },
                    { slug: "freelance", label: "Pour freelances" },
                    { slug: "suivi-depenses", label: "Suivi des dépenses" },
                    { slug: "automatisation", label: "Automatisation" },
                  ].map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-background-100 transition-colors text-foreground-700 text-sm font-sans"
                    >
                      <i className="ri-article-line text-foreground-400"></i>
                      {article.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}